/**
 * I/O layer for the DWD (Deutscher Wetterdienst) GeoServer WMS radar source
 * — region "eu-de", Germany-only "analysis" composite (M003 / D-006,
 * MULTI_REGION_RADAR.md). Mirrors wmsApi.ts/radarApi.ts's separation of pure
 * parsing/validation from fetch/URL-building, but two things differ from the
 * US WMS source and are NOT bugs:
 *
 *  1. DWD's `<Dimension name="time">` is a WMS "interval" triplet
 *     `START/END/PT_stepM` (a continuous ~3-day range), not a discrete
 *     comma-separated list — it must be EXPANDED to concrete slots.
 *  2. The advertised interval's END runs ~10 min into the future relative to
 *     real time (measured 2026-07-19,
 *     docs/research/EUROPE_RADAR_LIVE_MEASUREMENTS.md). The `wn-analysis`
 *     layer itself is observation-only (no nowcast), but the capability
 *     document's end tag is not reliably `<= now`, so every expansion is
 *     hard-clamped to `now` here — no frame past "now" may ever reach the
 *     timeline (D-006 §3 "uczciwość czasu").
 */
import { boundsToMercatorBbox, type LonLatBounds } from "./mercator";
import { RadarApiError } from "./radarApi";
import {
  RENDER_MATURITY_MS,
  MIN_ABS_PIXELS,
  PARTIAL_MEDIAN_RATIO,
  type CandidateEvaluation,
  type CandidateVerdict
} from "./frames";

export const DWD_WMS_BASE = "https://maps.dwd.de/geoserver/dwd/wms";

/** Observation-only composite; deliberately NOT `wn-product`/`rv-product` (those mix in a 2h nowcast — D-006 §3). */
export const DWD_LAYER = "dwd:Radar_wn-analysis_1x1km_ger";

/** Germany + margins (lon/lat) — the GetMap raster bbox, per DESIGN.md Aneks M003 §R3. Distinct from (narrower) `fitBounds` in sources.ts. */
export const DWD_BOUNDS: LonLatBounds = {
  west: 3.5,
  south: 46.2,
  east: 17.5,
  north: 55.6
};

/** Requested output width in px; height is derived from the bbox aspect ratio (same pattern as WMS_FRAME_WIDTH). */
export const DWD_FRAME_WIDTH = 1024;

export interface DwdTimeInterval {
  startMs: number;
  endMs: number;
  stepMs: number;
}

/**
 * Parse the DWD-style interval time dimension `START/END/PT_step_` out of a
 * layer-scoped GetCapabilities document. Returns null for anything that does
 * not match a well-formed 3-part interval (missing dimension, wrong shape,
 * unparseable dates/duration, or an inverted range) — never throws.
 */
export function parseDwdTimeDimension(
  xml: string,
  layerName: string = DWD_LAYER
): DwdTimeInterval | null {
  // The full GetCapabilities lists dozens of layers, each with its own time
  // dimension (some as discrete lists) — scope to OUR layer's section first,
  // otherwise the first dimension in the document wins and parsing fails.
  // The workspace-scoped endpoint (/geoserver/dwd/wms) advertises layer
  // names WITHOUT the "dwd:" prefix (verified live 2026-07-19), the global
  // /geoserver/ows endpoint WITH it — accept both.
  const bareName = layerName.includes(":") ? layerName.split(":")[1] : layerName;
  let layerIdx = xml.indexOf(`<Name>${layerName}</Name>`);
  if (layerIdx < 0) layerIdx = xml.indexOf(`<Name>${bareName}</Name>`);
  if (layerIdx < 0) return null;
  const layerEnd = xml.indexOf("</Layer>", layerIdx);
  const scoped = xml.slice(layerIdx, layerEnd > 0 ? layerEnd : layerIdx + 8000);
  const match = scoped.match(/<Dimension\s+[^>]*name="time"[^>]*>([\s\S]*?)<\/Dimension>/i);
  if (!match) return null;
  const parts = match[1].trim().split("/");
  if (parts.length !== 3) return null;
  const startMs = Date.parse(parts[0].trim());
  const endMs = Date.parse(parts[1].trim());
  const stepMs = parseIsoDurationMs(parts[2].trim());
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || stepMs === null) return null;
  if (endMs < startMs) return null;
  return { startMs, endMs, stepMs };
}

/** Parses the small subset of ISO 8601 durations DWD uses for its step (e.g. `PT5M`). Returns null for anything else or a non-positive result. */
function parseIsoDurationMs(iso: string): number | null {
  const m = iso.match(/^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/i);
  if (!m || (!m[1] && !m[2] && !m[3])) return null;
  const hours = Number(m[1] ?? 0);
  const minutes = Number(m[2] ?? 0);
  const seconds = Number(m[3] ?? 0);
  const ms = ((hours * 60 + minutes) * 60 + seconds) * 1000;
  return ms > 0 ? ms : null;
}

/**
 * Expand a start/end/step interval into a discrete, ascending epoch-ms
 * array, hard-clamped so nothing later than `nowMs` is ever returned (see
 * module header §2). Returns [] for a degenerate interval or one that is
 * entirely in the future.
 */
export function expandTimeInterval(
  startMs: number,
  endMs: number,
  stepMs: number,
  nowMs: number
): number[] {
  if (stepMs <= 0 || endMs < startMs) return [];
  const clampedEnd = Math.min(endMs, nowMs);
  if (clampedEnd < startMs) return [];
  const out: number[] = [];
  for (let t = startMs; t <= clampedEnd; t += stepMs) {
    out.push(t);
  }
  return out;
}

/**
 * Fetch GetCapabilities (no-store: an advertised interval is only useful
 * while current — same reasoning as fetchAdvertisedTimes/fetchTimeExtent)
 * and return the advertised, now-clamped analysis times, ascending.
 */
export async function fetchDwdTimes(signal?: AbortSignal): Promise<number[]> {
  const url = `${DWD_WMS_BASE}?service=WMS&version=1.3.0&request=GetCapabilities`;
  let res: Response;
  try {
    res = await fetch(url, { signal, cache: "no-store" });
  } catch (err) {
    throw new RadarApiError(
      `Nie udało się połączyć z serwisem DWD WMS: ${(err as Error).message}`
    );
  }
  if (!res.ok) {
    throw new RadarApiError(`Serwis DWD WMS zwrócił błąd HTTP ${res.status}`);
  }
  const xml = await res.text();
  const interval = parseDwdTimeDimension(xml);
  if (interval === null) {
    throw new RadarApiError("Odpowiedź serwisu DWD WMS nie zawiera poprawnego wymiaru czasu.");
  }
  const times = expandTimeInterval(interval.startMs, interval.endMs, interval.stepMs, Date.now());
  if (times.length === 0) {
    throw new RadarApiError("Serwis DWD WMS nie udostępnia obecnie żadnej klatki analizy sprzed teraz.");
  }
  return times;
}

/**
 * Build the GetMap URL for one DWD analysis frame. `timeMs` should be one of
 * the values returned by fetchDwdTimes/expandTimeInterval (a 5-min-grid
 * time within the advertised, now-clamped interval); the advertised times
 * carry a zero millisecond component so `toISOString()` reproduces the grid
 * time verbatim, same as buildWmsFrameUrl. `cacheBusterMs` mirrors the other
 * sources' retry pattern (appends an inert `_cb` param).
 */
export function buildDwdFrameUrl(timeMs: number, cacheBusterMs?: number): string {
  const bbox = boundsToMercatorBbox(DWD_BOUNDS);
  const aspect = (bbox.xmax - bbox.xmin) / (bbox.ymax - bbox.ymin);
  const width = DWD_FRAME_WIDTH;
  const height = Math.round(width / aspect);
  const params = new URLSearchParams({
    service: "WMS",
    version: "1.3.0",
    request: "GetMap",
    layers: DWD_LAYER,
    crs: "EPSG:3857",
    bbox: `${bbox.xmin},${bbox.ymin},${bbox.xmax},${bbox.ymax}`,
    width: String(width),
    height: String(height),
    format: "image/png",
    transparent: "true",
    time: new Date(Math.round(timeMs)).toISOString()
  });
  if (cacheBusterMs !== undefined) {
    params.set("_cb", String(cacheBusterMs));
  }
  return `${DWD_WMS_BASE}?${params.toString()}`;
}

/**
 * ---- DWD candidate validation (D-006 §4, MULTI_REGION_RADAR.md) ----
 *
 * PROVISIONAL CALIBRATION NOTE: `maps.dwd.de` is not on this implementation
 * session's network allowlist, so the constant below could not be measured
 * live here (unlike the WMS/legend constants in frames.ts, which Lead
 * measured directly against the real NOAA service). It is derived instead
 * from Lead's live probe recorded in
 * docs/research/EUROPE_RADAR_LIVE_MEASUREMENTS.md (2026-07-19,
 * ~17:40–18:30Z): at `transparent=true`, a 300×340 test canvas showed ~70%
 * fully transparent pixels and ~29–30% colored (echoes + the always-present
 * magenta coverage-boundary outline) — i.e. that sample upper-bounds
 * "boundary + whatever rain was falling at that moment", not the boundary
 * alone, so it cannot be used directly as a pure-boundary floor without
 * risking deferring real light-rain frames forever. The constant is
 * CALIBRATED LIVE by Lead (2026-07-19 ~18:50Z) against the real service at
 * the exact validation sample size (256×172, the resolution
 * `countVisiblePixels` decodes at — radarApi.ts): a live frame decomposed
 * into gray no-data mask = 14 313 px, magenta coverage outline = 1 223 px
 * (constant, bbox- and sample-size-locked), echoes = variable. The constant
 * below = mask+outline (15 536) + antialias margin. Ultra-trace echoes
 * (<~100 px at this scale) may be masked by the margin → worst case a
 * tip frame defers until maturity (≤15 min); acceptable and honest.
 * Re-measure if DWD_BOUNDS, the sample width or DWD's style ever change.
 */
export const DWD_BOUNDARY_BASELINE_PIXELS = 15_600;

function baselineAdjust(n: number): number {
  return Math.max(0, n - DWD_BOUNDARY_BASELINE_PIXELS);
}

/**
 * DWD-specific candidate validation. Reuses the US sources' maturity/partial
 * -mosaic reasoning (RENDER_MATURITY_MS, MIN_ABS_PIXELS, PARTIAL_MEDIAN_RATIO
 * from frames.ts) but operates on BASELINE-adjusted pixel counts, because the
 * DWD coverage-boundary ring + outline colour is always present — raw
 * pixelCount can never reach 0 even over a completely dry Germany.
 *
 * Two deliberate differences from evaluateCandidate (US, frames.ts):
 *  - NO physical-continuity guard (no EMPTY_PLAUSIBLE_MEDIAN_MAX check): a
 *    mature, baseline-adjusted-to-zero frame is always accepted. Germany's
 *    much smaller domain than CONUS genuinely can go from rain to fully dry
 *    between two 5-min polls; treating that as "server lag" the way the US
 *    nationwide-coverage assumption does would misreport real weather.
 *  - the byte-identical-duplicate check compares RAW byteSize/pixelCount
 *    (server time-snapping produces byte-identical payloads regardless of
 *    any baseline; comparing raw values is equivalent to comparing adjusted
 *    ones here since both sides shift by the same constant).
 */
export function validateDwdFrame(stats: CandidateEvaluation): CandidateVerdict {
  if (stats.pixelCount < 0) return "invalid";
  const adjPixels = baselineAdjust(stats.pixelCount);
  if (stats.ageMs >= RENDER_MATURITY_MS) {
    return "accept"; // mature: accept even at adjusted-zero — genuinely echo-free Germany is valid data
  }
  if (adjPixels === 0) return "defer"; // immature + nothing beyond the boundary ring — not proven rendered yet
  const adjMedian = baselineAdjust(stats.windowMedianPixels);
  const floor = Math.max(MIN_ABS_PIXELS, Math.floor(adjMedian * PARTIAL_MEDIAN_RATIO));
  if (adjMedian > 0 && adjPixels < floor) return "defer";
  if (
    stats.prevByteSize !== undefined &&
    stats.byteSize === stats.prevByteSize &&
    stats.pixelCount === stats.prevPixelCount
  ) {
    return "defer"; // byte-identical to previous accepted frame — server time-snapping duplicate
  }
  return "accept";
}

/**
 * ---- Cheap polling path (M003, Lead) ----
 * DWD's analysis grid is FIXED: 5-min slots anchored at 00:00 UTC. A poll
 * therefore does not need the 850 KB GetCapabilities document every 60 s
 * (that would be ~50 MB/h of pure metadata) — slot times are computable
 * locally, and the D-004 validation gate (defer on empty/partial) already
 * protects against requesting a slot the server has not rendered yet.
 * GetCapabilities is still fetched at init (layer sanity + real extent) and
 * re-synced at most once per DWD_CAPS_RESYNC_MS.
 */
export const DWD_GRID_STEP_MS = 5 * 60 * 1000;
export const DWD_CAPS_RESYNC_MS = 60 * 60 * 1000;

let lastCapsFetchMs = 0;

/** Local, network-free expansion of the fixed 5-min UTC grid over [now-windowMs, now]. */
export function computeDwdGridTimes(nowMs: number, windowMs: number): number[] {
  const end = Math.floor(nowMs / DWD_GRID_STEP_MS) * DWD_GRID_STEP_MS;
  const start = end - Math.floor(windowMs / DWD_GRID_STEP_MS) * DWD_GRID_STEP_MS;
  const out: number[] = [];
  for (let t = start; t <= end; t += DWD_GRID_STEP_MS) out.push(t);
  return out;
}

/**
 * availableTimes for the DWD source: full GetCapabilities on first call and
 * at most hourly afterwards; local grid computation in between (polls).
 */
export async function dwdAvailableTimes(
  windowMs: number,
  signal?: AbortSignal
): Promise<number[]> {
  const now = Date.now();
  if (now - lastCapsFetchMs > DWD_CAPS_RESYNC_MS) {
    const times = await fetchDwdTimes(signal);
    lastCapsFetchMs = now;
    // Intersect the advertised range with our window, keep grid alignment.
    const cutoff = now - windowMs;
    return times.filter((t) => t >= cutoff);
  }
  return computeDwdGridTimes(now, windowMs);
}
